import * as React from "react"
import { useSelector, useDispatch, shallowEqual } from "react-redux"

import Biography from "../components/Dashboard/Biography"
import EntrySummary from "../components/Dashboard/EntrySummary"
import EntryAddition from "../components/Dashboard/EntryAddition"
import "../components/Dashboard/dashboard.scss"
import PrimaryButton from "../components/PrimaryButton"

import { ReducerState } from "AppTypes"
import { appActions, userInfoActions } from "../actions"
import getStepConfig from "./stepConfig"
import { IEntry } from "../types/appDataTypes"
import { retrieveDocuments } from "../utils/retrieveDocuments"

import api from "../utils/api"
import { updateLanguageAATagging, updateAATagging } from "../utils/aatagging"

const DashboardContainer = () => {
    const dispatch = useDispatch()

    const { userInfo, artEntry, isCallingApi, pictureStore, langPack } = useSelector((store: ReducerState) => ({
        userInfo: store.userInfo,
        artEntry: (store.userInfo.artEntry) || [] as IEntry[],
        isCallingApi: store.app.isCallingApi,
        pictureStore: store.app.pictureStore,
        langPack: store.config.langPack
    }), shallowEqual)

    const [showWarning, setShowWarning] = React.useState([])
    const [firstLoad, setFirstLoad] = React.useState(true)

    const checkApplication = async () => {
        // check and sync application
        const { appId } = userInfo

        const userInfoResponse = await api.getApplication(appId)
        if (userInfoResponse && userInfoResponse.status === 200) {
            const { data } = userInfoResponse
            data.appId = appId
            if (
                (data.artEntry && userInfo.artEntry) &&
                (data.artEntry.length > userInfo.artEntry.length)
            ) retrieveDocuments(data)

            dispatch(userInfoActions.updateUserInfo({ ...data }))
        }
    }

    React.useEffect(() => {
        checkApplication()
        // eslint-disable-next-line
    }, [])

    const checkEntryComplete = (entry: IEntry) => {
        let result = true
        const checkList: (keyof IEntry)[] = ["title", "medium", "height", "width", "description"]

        for (let i = 0; i < checkList.length; i++) {
            if (!entry[checkList[i]] || entry[checkList[i]] === "") result = false
        }

        if (!pictureStore[entry.entryId] || !pictureStore[entry.entryId]["p1"]) result = false

        return result
    }

    const nextStepHandler = () => {
        let isValid = true
        if (firstLoad) setFirstLoad(false);
        if (!userInfo.artistInfo.biography) {
            isValid = false
        }

        const list: boolean[] = []
        for (let index in artEntry) {
            if (!checkEntryComplete(artEntry[index])) {
                list[index] = true
                isValid = false
            }
        }
        setShowWarning(list)

        if (isValid) {
            const { nextStep } = getStepConfig()
            dispatch(appActions.gotoStep(nextStep))

            // AA Tracking
            const AALang = updateLanguageAATagging(langPack["lang"])
            updateAATagging({
                "event_name": "generic_form_complete_step8",
                "form_name": "UOB PoY",
                "step_name": "upload_image",
                "language": AALang,
                "country": userInfo.activeCountry.toLowerCase()
            }, "other_form_step")
        }
    }

    const addEntryHandler = () => dispatch(appActions.gotoStep("EntryEditor", { entryIndex: artEntry.length }))


    return <React.Fragment>
        <div className="dashboard-container">
            <div className="dashboard-content">
                <div className="user-icon"></div>
                <span className="dashboard__name">
                    {userInfo.fullName || ""}
                </span>
            </div>
            <Biography firstLoad={firstLoad} />
            <div className="dashboard__entries">
                {(artEntry && !!artEntry.length)
                    && <div className="dashboard__entries__header">
                        <h2 className="dashboard__title">{langPack["artworkEntry"]}</h2>
                    </div>
                }
                {artEntry.map((entry, key) => <EntrySummary
                    key={key}
                    entry={entry}
                    entryIndex={key}
                    isInvalid={showWarning[key]}
                />)}
                {artEntry.length < 1
                    && <EntryAddition
                        actionHandler={addEntryHandler}
                        existing={artEntry ? artEntry.length : 0}
                    />
                }
            </div>
        </div>
        {artEntry && artEntry.length > 0
            && <div className="action">
                {langPack["competitionDisclaimer"] &&
                    <>
                        <p className="app-disclaimer">{langPack["competitionDisclaimerText"]}</p>
                        <br />
                    </>
                }
                <PrimaryButton
                    text={langPack["buttonNext"]}
                    onClick={nextStepHandler}
                    disabled={isCallingApi}
                />
            </div>}
        {artEntry && artEntry.length == 0 &&
            <div className="action">
                {langPack["competitionDisclaimer"] &&
                    <>
                        <p className="app-disclaimer">{langPack["competitionDisclaimerText"]}</p>
                        <br />
                    </>
                }
            </div>
        }
    </React.Fragment>
}

export default DashboardContainer